import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminModerationService } from 'api/services/AdminModerationService';
import type { ContentReportListItemDto } from 'api/types/moderation/ContentReportListItemDto';
import type { BannedUserListItemDto } from 'api/types/moderation/BannedUserListItemDto';
import type { ModerationUserSearchItemDto } from 'api/types/moderation/ModerationUserSearchItemDto';
import { ReportContentType, ContentReportStatus } from 'api/types/moderation';
import {
  coerceContentReportStatus,
  coerceReportContentType,
  isPendingReportStatus,
} from 'utils/moderationEnums';

const USER_SEARCH_DEBOUNCE_MS = 300;

function contentTypeLabel(t: ReportContentType | string | number): string {
  const coerced = coerceReportContentType(t) ?? (t as ReportContentType);
  switch (coerced) {
    case ReportContentType.Profile:
      return 'Profil';
    case ReportContentType.Listing:
      return 'İlan';
    case ReportContentType.Reel:
      return 'Reel';
    default:
      return String(t);
  }
}

function reportStatusLabel(status: ContentReportStatus | string | number): string {
  const coerced = coerceContentReportStatus(status) ?? (status as ContentReportStatus);
  switch (coerced) {
    case ContentReportStatus.Pending:
      return 'Bekliyor';
    case ContentReportStatus.Resolved:
      return 'Çözüldü';
    case ContentReportStatus.Dismissed:
      return 'Reddedildi';
    default:
      return String(status);
  }
}

const ModerationDashboardPage: React.FC = () => {
  const [tab, setTab] = useState<'reports' | 'bans'>('reports');
  const [reports, setReports] = useState<ContentReportListItemDto[]>([]);
  const [bans, setBans] = useState<BannedUserListItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banReason, setBanReason] = useState('');
  const [banSubmitting, setBanSubmitting] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<ModerationUserSearchItemDto[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedBanUser, setSelectedBanUser] = useState<ModerationUserSearchItemDto | null>(null);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const userSearchSeq = useRef(0);
  const [reportActionId, setReportActionId] = useState<string | null>(null);
  const [resolveModalReport, setResolveModalReport] = useState<ContentReportListItemDto | null>(null);
  const [dismissModalReport, setDismissModalReport] = useState<ContentReportListItemDto | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [deactivateListing, setDeactivateListing] = useState(false);
  const [banUserOnResolve, setBanUserOnResolve] = useState(false);
  const [banReasonOnResolve, setBanReasonOnResolve] = useState('');
  const [rejectReelOnResolve, setRejectReelOnResolve] = useState(false);
  const [reportActionLoading, setReportActionLoading] = useState(false);
  const [reportStatusFilter, setReportStatusFilter] = useState<'all' | ContentReportStatus>(
    ContentReportStatus.Pending
  );
  const [inspectReport, setInspectReport] = useState<ContentReportListItemDto | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statusParam =
        reportStatusFilter === 'all' ? undefined : reportStatusFilter;
      const [r, b] = await Promise.all([
        adminModerationService.listReports(statusParam),
        adminModerationService.listBans(),
      ]);
      if (r.isSuccess && r.data) setReports(r.data);
      else setReports([]);
      if (b.isSuccess && b.data) setBans(b.data);
      else setBans([]);
      const errMsg = [!r.isSuccess ? r.message : null, !b.isSuccess ? b.message : null].filter(Boolean).join(' ');
      if (errMsg) setError(errMsg);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Yükleme hatası.');
    } finally {
      setLoading(false);
    }
  }, [reportStatusFilter]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (selectedBanUser) {
      setUserSearchResults([]);
      setUserSearchLoading(false);
      return;
    }
    const q = userSearchQuery.trim();
    if (q.length < 2) {
      userSearchSeq.current += 1;
      setUserSearchResults([]);
      setUserSearchLoading(false);
      return;
    }

    const timer = window.setTimeout(() => {
      const mySeq = ++userSearchSeq.current;
      setUserSearchLoading(true);
      void adminModerationService
        .searchUsers(q)
        .then((res) => {
          if (mySeq !== userSearchSeq.current) return;
          if (res.isSuccess && res.data) setUserSearchResults(res.data);
          else setUserSearchResults([]);
          setUserSearchLoading(false);
        })
        .catch(() => {
          if (mySeq !== userSearchSeq.current) return;
          setUserSearchResults([]);
          setUserSearchLoading(false);
        });
    }, USER_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [userSearchQuery, selectedBanUser]);

  const handleCreateBan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBanUser?.id || !banReason.trim() || banSubmitting) return;
    setBanSubmitting(true);
    try {
      const res = await adminModerationService.createBan({
        userId: selectedBanUser.id,
        reason: banReason.trim(),
      });
      if (res.isSuccess) {
        userSearchSeq.current += 1;
        setSelectedBanUser(null);
        setUserSearchQuery('');
        setUserSearchResults([]);
        setBanReason('');
        await loadAll();
      } else {
        window.alert(res.message ?? 'Ban oluşturulamadı.');
      }
    } finally {
      setBanSubmitting(false);
    }
  };

  const handleRevoke = async (banRowId: string) => {
    if (!window.confirm('Bu banı kaldırmak istediğinize emin misiniz?')) return;
    setRevokingId(banRowId);
    try {
      const res = await adminModerationService.revokeBan(banRowId);
      if (res.isSuccess) await loadAll();
      else window.alert(res.message ?? 'İşlem başarısız.');
    } finally {
      setRevokingId(null);
    }
  };

  const contentLinkFor = (row: ContentReportListItemDto): string | null => {
    if (!row.contentId) return null;
    switch (coerceReportContentType(row.contentType)) {
      case ReportContentType.Listing:
        return `/ilan-detay/${row.contentId}`;
      case ReportContentType.Reel:
        return '/gonderiler';
      case ReportContentType.Profile:
        return `/satici-profil/${row.reportedId}`;
      default:
        return null;
    }
  };

  const openResolveModal = (row: ContentReportListItemDto) => {
    setResolveModalReport(row);
    setResolutionNote('');
    setDeactivateListing(false);
    setBanUserOnResolve(false);
    setBanReasonOnResolve('');
    setRejectReelOnResolve(false);
  };

  const openDismissModal = (row: ContentReportListItemDto) => {
    setDismissModalReport(row);
    setResolutionNote('');
  };

  const handleResolveReport = async () => {
    if (!resolveModalReport || reportActionLoading) return;
    setReportActionLoading(true);
    setReportActionId(resolveModalReport.id);
    try {
      const res = await adminModerationService.resolveReport(resolveModalReport.id, {
        resolutionNote: resolutionNote.trim() || null,
        deactivateListing:
          coerceReportContentType(resolveModalReport.contentType) === ReportContentType.Listing &&
          deactivateListing,
        banUser: banUserOnResolve,
        banReason: banUserOnResolve ? banReasonOnResolve.trim() || null : null,
        rejectReel:
          coerceReportContentType(resolveModalReport.contentType) === ReportContentType.Reel &&
          rejectReelOnResolve,
      });
      if (res.isSuccess) {
        setResolveModalReport(null);
        await loadAll();
      } else {
        window.alert(res.message ?? 'Şikayet çözülemedi.');
      }
    } finally {
      setReportActionLoading(false);
      setReportActionId(null);
    }
  };

  const handleDismissReport = async () => {
    if (!dismissModalReport || reportActionLoading) return;
    setReportActionLoading(true);
    setReportActionId(dismissModalReport.id);
    try {
      const res = await adminModerationService.dismissReport(dismissModalReport.id, {
        resolutionNote: resolutionNote.trim() || null,
      });
      if (res.isSuccess) {
        setDismissModalReport(null);
        await loadAll();
      } else {
        window.alert(res.message ?? 'Şikayet reddedilemedi.');
      }
    } finally {
      setReportActionLoading(false);
      setReportActionId(null);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">Moderasyon</h1>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => void loadAll()} disabled={loading}>
          Yenile
        </button>
      </div>

      {error && (
        <div className="alert alert-warning" role="alert">
          {error}
        </div>
      )}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${tab === 'reports' ? 'active' : ''}`}
            onClick={() => setTab('reports')}
          >
            Şikayetler
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${tab === 'bans' ? 'active' : ''}`}
            onClick={() => setTab('bans')}
          >
            Aktif banlar
          </button>
        </li>
      </ul>

      {loading && (
        <div className="py-4 text-center text-muted">
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Yükleniyor…
        </div>
      )}

      {!loading && tab === 'reports' && (
        <>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <label className="small text-muted mb-0" htmlFor="mod-report-status-filter">Durum:</label>
          <select
            id="mod-report-status-filter"
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={reportStatusFilter}
            onChange={(e) => {
              const v = e.target.value;
              setReportStatusFilter(
                v === 'all' ? 'all' : Number(v) as ContentReportStatus,
              );
            }}
          >
            <option value="all">Tümü</option>
            <option value={ContentReportStatus.Pending}>Bekliyor</option>
            <option value={ContentReportStatus.Resolved}>Çözüldü</option>
            <option value={ContentReportStatus.Dismissed}>Reddedildi</option>
          </select>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-striped align-middle">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Şikayet eden</th>
                <th>Şikayet edilen</th>
                <th>Ticari unvan</th>
                <th>Tür</th>
                <th>Durum</th>
                <th>İçerik</th>
                <th>Mesaj</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-muted text-center py-4">
                    Kayıt yok.
                  </td>
                </tr>
              ) : (
                reports.map((row) => (
                  <tr key={row.id}>
                    <td className="text-nowrap small">
                      {row.createdAt ? new Date(row.createdAt).toLocaleString('tr-TR') : '—'}
                    </td>
                    <td className="small">{row.reporterDisplayName ?? '—'}</td>
                    <td className="small">{row.reportedDisplayName ?? '—'}</td>
                    <td className="small">{row.reportedCorporateTradeTitle ?? '—'}</td>
                    <td>{contentTypeLabel(row.contentType as ReportContentType)}</td>
                    <td>
                      <span className="badge bg-secondary">
                        {reportStatusLabel(row.status)}
                      </span>
                    </td>
                    <td className="small">{row.contentDisplayLabel ?? '—'}</td>
                    <td className="small" style={{ maxWidth: 280 }}>
                      {row.message ? (
                        <span title={row.message}>{row.message.length > 120 ? `${row.message.slice(0, 120)}…` : row.message}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="text-nowrap text-end">
                      <div className="d-flex flex-wrap gap-1 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => setInspectReport(row)}
                        >
                          İncele
                        </button>
                        {isPendingReportStatus(row.status) && (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline-success btn-sm"
                              disabled={reportActionLoading && reportActionId === row.id}
                              onClick={() => openResolveModal(row)}
                            >
                              Çöz
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              disabled={reportActionLoading && reportActionId === row.id}
                              onClick={() => openDismissModal(row)}
                            >
                              Reddet
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {!loading && tab === 'bans' && (
        <>
          <div className="card mb-4">
            <div className="card-header">Yeni ban (kullanıcıyı platformdan uzaklaştır)</div>
            <div className="card-body">
              <form className="row g-2 align-items-end" onSubmit={handleCreateBan}>
                <div className="col-md-5">
                  <label className="form-label small mb-0" htmlFor="mod-ban-user-search">
                    Kullanıcı
                  </label>
                  <div className="position-relative">
                    {selectedBanUser && (
                      <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
                        <span className="badge bg-secondary text-wrap text-start">{selectedBanUser.displayName}</span>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => {
                            userSearchSeq.current += 1;
                            setSelectedBanUser(null);
                            setUserSearchQuery('');
                            setUserSearchResults([]);
                          }}
                        >
                          Değiştir
                        </button>
                      </div>
                    )}
                    <input
                      id="mod-ban-user-search"
                      className="form-control form-control-sm"
                      value={userSearchQuery}
                      onChange={(e) => {
                        setUserSearchQuery(e.target.value);
                        setUserPickerOpen(true);
                      }}
                      onFocus={() => setUserPickerOpen(true)}
                      onBlur={() => {
                        window.setTimeout(() => setUserPickerOpen(false), 200);
                      }}
                      placeholder="İsim veya e-posta yazın (en az 2 karakter)"
                      autoComplete="off"
                      disabled={!!selectedBanUser}
                      aria-autocomplete="list"
                      aria-expanded={userPickerOpen && !selectedBanUser}
                    />
                    {userPickerOpen && !selectedBanUser && userSearchQuery.trim().length >= 2 && (
                      <div
                        className="border rounded shadow-sm position-absolute w-100 mt-1"
                        style={{ zIndex: 20, maxHeight: 240, overflowY: 'auto', backgroundColor: 'var(--sb-bg)' }}
                        onMouseDown={(e) => e.preventDefault()}
                        role="listbox"
                      >
                        {userSearchLoading && (
                          <div className="px-3 py-2 text-muted small">
                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                            Aranıyor…
                          </div>
                        )}
                        {!userSearchLoading && userSearchResults.length === 0 && (
                          <div className="px-3 py-2 text-muted small">Sonuç yok.</div>
                        )}
                        {!userSearchLoading &&
                          userSearchResults.map((u) => (
                            <button
                              key={`${u.accountKind}-${u.id}`}
                              type="button"
                              role="option"
                              className="d-block w-100 text-start btn btn-light btn-sm rounded-0 border-0 border-bottom small py-2"
                              onClick={() => {
                                setSelectedBanUser(u);
                                setUserSearchQuery('');
                                setUserSearchResults([]);
                                setUserPickerOpen(false);
                              }}
                            >
                              <span className="d-block fw-medium">{u.displayName}</span>
                              <span className="text-muted">
                                {u.accountKind === 'corporate' ? 'Kurumsal' : 'Bireysel'} · {u.email}
                              </span>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-5">
                  <label className="form-label small mb-0" htmlFor="mod-ban-reason">
                    Gerekçe
                  </label>
                  <input
                    id="mod-ban-reason"
                    className="form-control form-control-sm"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-2">
                  <button
                    type="submit"
                    className="btn btn-danger btn-sm w-100"
                    disabled={banSubmitting || !selectedBanUser}
                  >
                    {banSubmitting ? '…' : 'Ban uygula'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-striped align-middle">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Banlanan kullanıcı</th>
                  <th>Admin</th>
                  <th>Gerekçe</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {bans.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted text-center py-4">
                      Aktif ban yok.
                    </td>
                  </tr>
                ) : (
                  bans.map((row) => (
                    <tr key={row.id}>
                      <td className="text-nowrap small">
                        {row.createdAt ? new Date(row.createdAt).toLocaleString('tr-TR') : '—'}
                      </td>
                      <td className="small">{row.bannedUserDisplayName ?? '—'}</td>
                      <td className="small">{row.adminDisplayName ?? '—'}</td>
                      <td className="small">{row.reason}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-outline-warning btn-sm"
                          onClick={() => void handleRevoke(row.id)}
                          disabled={revokingId === row.id}
                        >
                          {revokingId === row.id ? '…' : 'Ban kaldır'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {resolveModalReport && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Şikayeti çöz</h5>
                <button type="button" className="btn-close" onClick={() => setResolveModalReport(null)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small" htmlFor="mod-resolve-note">
                    Not (isteğe bağlı)
                  </label>
                  <textarea
                    id="mod-resolve-note"
                    className="form-control form-control-sm"
                    rows={3}
                    value={resolutionNote}
                    onChange={(e) => setResolutionNote(e.target.value)}
                  />
                </div>
                {coerceReportContentType(resolveModalReport.contentType) === ReportContentType.Listing && (
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="mod-deactivate-listing"
                      checked={deactivateListing}
                      onChange={(e) => setDeactivateListing(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="mod-deactivate-listing">
                      İlanı pasifleştir
                    </label>
                  </div>
                )}
                {coerceReportContentType(resolveModalReport.contentType) === ReportContentType.Reel && (
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="mod-reject-reel"
                      checked={rejectReelOnResolve}
                      onChange={(e) => setRejectReelOnResolve(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="mod-reject-reel">
                      Reel gönderisini reddet (onay bekliyorsa)
                    </label>
                  </div>
                )}
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mod-ban-user"
                    checked={banUserOnResolve}
                    onChange={(e) => setBanUserOnResolve(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="mod-ban-user">
                    Kullanıcıyı platformdan uzaklaştır (ban)
                  </label>
                </div>
                {banUserOnResolve && (
                  <div className="mb-2">
                    <label className="form-label small" htmlFor="mod-ban-reason-resolve">
                      Ban gerekçesi
                    </label>
                    <input
                      id="mod-ban-reason-resolve"
                      className="form-control form-control-sm"
                      value={banReasonOnResolve}
                      onChange={(e) => setBanReasonOnResolve(e.target.value)}
                      required
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setResolveModalReport(null)}>
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  disabled={reportActionLoading}
                  onClick={() => void handleResolveReport()}
                >
                  {reportActionLoading ? '…' : 'Çöz'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {inspectReport && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Şikayet detayı</h5>
                <button type="button" className="btn-close" onClick={() => setInspectReport(null)} />
              </div>
              <div className="modal-body small">
                <dl className="row mb-0">
                  <dt className="col-sm-3">Durum</dt>
                  <dd className="col-sm-9">{reportStatusLabel(inspectReport.status as ContentReportStatus)}</dd>
                  <dt className="col-sm-3">Tür</dt>
                  <dd className="col-sm-9">{contentTypeLabel(inspectReport.contentType as ReportContentType)}</dd>
                  <dt className="col-sm-3">İçerik</dt>
                  <dd className="col-sm-9">{inspectReport.contentDisplayLabel ?? '—'}</dd>
                  <dt className="col-sm-3">Şikayet eden</dt>
                  <dd className="col-sm-9">{inspectReport.reporterDisplayName ?? '—'}</dd>
                  <dt className="col-sm-3">Şikayet edilen</dt>
                  <dd className="col-sm-9">{inspectReport.reportedDisplayName ?? '—'}</dd>
                  <dt className="col-sm-3">Ticari unvan</dt>
                  <dd className="col-sm-9">{inspectReport.reportedCorporateTradeTitle ?? '—'}</dd>
                  <dt className="col-sm-3">Mesaj</dt>
                  <dd className="col-sm-9">{inspectReport.message ?? '—'}</dd>
                  {inspectReport.resolutionNote && (
                    <>
                      <dt className="col-sm-3">Çözüm notu</dt>
                      <dd className="col-sm-9">{inspectReport.resolutionNote}</dd>
                    </>
                  )}
                </dl>
                {contentLinkFor(inspectReport) && (
                  <a
                    className="btn btn-outline-primary btn-sm mt-2"
                    href={contentLinkFor(inspectReport)!}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    İçeriği görüntüle
                  </a>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setInspectReport(null)}>
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {dismissModalReport && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Şikayeti reddet</h5>
                <button type="button" className="btn-close" onClick={() => setDismissModalReport(null)} />
              </div>
              <div className="modal-body">
                <label className="form-label small" htmlFor="mod-dismiss-note">
                  Not (isteğe bağlı)
                </label>
                <textarea
                  id="mod-dismiss-note"
                  className="form-control form-control-sm"
                  rows={3}
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDismissModalReport(null)}>
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-warning btn-sm"
                  disabled={reportActionLoading}
                  onClick={() => void handleDismissReport()}
                >
                  {reportActionLoading ? '…' : 'Reddet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModerationDashboardPage;
